// ─── Finishing Preview List Service ───
// Fetches the current user's AI finishing preview jobs + generated images
// from Supabase and maps them into the AiPreview list shape used by the
// dashboard UI.
import { getSupabaseClient } from '@/lib/supabase';
import type { AiPreview } from '@/mocks/dashboard';

interface FinishingJobRow {
  id: string;
  product_id: string;
  status: string;
  result_id: string | null;
  structured_lines_json: { part_code: string; finish_code: string; color_code: string }[] | null;
  created_at: string;
}

interface ImageRow {
  public_url?: string | null;
  image_url?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

// Resolve a usable image URL from a row that may store a direct URL,
// a public URL, or a private storage reference (needs a signed URL).
async function resolveImageUrl(row: ImageRow | null | undefined): Promise<string> {
  if (!row) return '';
  if (row.public_url) return row.public_url;
  if (row.image_url) return row.image_url;

  const supabase = getSupabaseClient();
  if (!supabase || !row.storage_bucket || !row.storage_path) return '';

  try {
    const { data, error } = await supabase.storage
      .from(row.storage_bucket)
      .createSignedUrl(row.storage_path, 3600);
    if (!error && data?.signedUrl) return data.signedUrl;
  } catch {
    // signed URL failed — fall through to public URL
  }

  try {
    const { data } = supabase.storage
      .from(row.storage_bucket)
      .getPublicUrl(row.storage_path);
    if (data?.publicUrl) return data.publicUrl;
  } catch {
    // public URL also failed
  }

  return '';
}

function mapStatus(status: string): AiPreview['status'] {
  switch (status) {
    case 'pending':
    case 'queued':
      return 'queued';
    case 'processing':
      return 'processing';
    case 'completed':
      return 'completed';
    default:
      return 'failed';
  }
}

export async function getFinishingPreviewList(): Promise<AiPreview[]> {
  const supabase = getSupabaseClient();
  if (!supabase) return [];

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  // 1. Fetch the user's finishing preview jobs (newest first)
  const { data: jobs, error: jobsError } = await supabase
    .from('finishing_preview_jobs')
    .select('id, product_id, status, result_id, structured_lines_json, created_at')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (jobsError) {
    console.error('[FinishingPreviewList] Failed to fetch jobs:', jobsError.message);
    return [];
  }
  if (!jobs || jobs.length === 0) return [];

  const jobRows = jobs as FinishingJobRow[];

  const productIds = Array.from(new Set(jobRows.map((j) => j.product_id)));
  const resultIds = Array.from(
    new Set(jobRows.filter((j) => j.result_id).map((j) => j.result_id as string)),
  );

  // 2. Fetch product names + finishing descriptions
  const { data: products } = productIds.length
    ? await supabase
        .from('products')
        .select('id, name, finishing_method_description')
        .in('id', productIds)
    : { data: [] as { id: string; name: string; finishing_method_description: string | null }[] };

  // 3. Fetch original working images (thumbnails)
  const { data: workingImages } = productIds.length
    ? await supabase
        .from('product_images')
        .select('product_id, public_url, storage_bucket, storage_path')
        .eq('image_role', 'working')
        .in('product_id', productIds)
    : { data: [] as ({ product_id: string } & ImageRow)[] };

  // 4. Fetch generated result images
  const { data: results } = resultIds.length
    ? await supabase
        .from('finishing_preview_results')
        .select('id, public_url, image_url, storage_bucket, storage_path')
        .in('id', resultIds)
    : { data: [] as ({ id: string } & ImageRow)[] };

  // 5. Fetch master codes to resolve part / finish names
  const { data: masterCodes } = await supabase
    .from('master_codes')
    .select('code_type, code, name')
    .in('code_type', ['container_part', 'finish_l1', 'print']);

  const partNames = new Map<string, string>();
  const finishNames = new Map<string, string>();
  const printNames = new Map<string, string>();
  if (masterCodes) {
    for (const row of masterCodes) {
      if (row.code_type === 'container_part') partNames.set(row.code as string, row.name as string);
      else if (row.code_type === 'finish_l1') finishNames.set(row.code as string, row.name as string);
      else if (row.code_type === 'print') printNames.set(row.code as string, row.name as string);
    }
  }

  const productMap = new Map((products || []).map((p) => [p.id, p]));
  const workingMap = new Map<string, ImageRow>();
  for (const img of workingImages || []) {
    if (!workingMap.has(img.product_id)) workingMap.set(img.product_id, img);
  }
  const resultMap = new Map((results || []).map((r) => [r.id, r]));

  // 6. Resolve image URLs (parallel to avoid slow serial waits)
  const imageUrlCache = new Map<string, string>();
  const resolveCached = async (key: string, row: ImageRow | null | undefined) => {
    if (!row) return '';
    if (!imageUrlCache.has(key)) {
      imageUrlCache.set(key, await resolveImageUrl(row));
    }
    return imageUrlCache.get(key) || '';
  };

  const urlTasks = jobRows.map(async (job) => {
    const original = await resolveCached(`work-${job.product_id}`, workingMap.get(job.product_id));
    const resultRow = job.result_id ? resultMap.get(job.result_id) : null;
    const generated = await resolveCached(`result-${job.result_id}`, resultRow);
    return { job, original, generated };
  });

  const resolved = await Promise.all(urlTasks);

  // 7. Build final list items
  return resolved.map(({ job, original, generated }) => {
    const product = productMap.get(job.product_id);
    const productName = product?.name || 'Untitled product';

    // Build a friendly finish summary from the structured lines
    const lines = job.structured_lines_json || [];
    let finishType = product?.finishing_method_description || '';
    if (!finishType && lines.length > 0) {
      finishType = lines
        .map((line) => {
          const part = partNames.get(line.part_code) || '';
          const finish = finishNames.get(line.finish_code) || printNames.get(line.finish_code) || '';
          return [part, finish].filter(Boolean).join(' ');
        })
        .filter(Boolean)
        .join(' · ');
    }
    if (!finishType) finishType = '후가공 미리보기';

    return {
      id: job.id,
      productName,
      originalImage: original,
      generatedImage: generated,
      finishType,
      status: mapStatus(job.status),
      quoteRequestId: null,
      createdAt: job.created_at,
    } as AiPreview;
  });
}